/**
 * TechHub Shopify Theme — theme.js
 * Handles: mobile nav, variant selection, product image gallery,
 *          quantity selectors, cart drawer, animations, sticky header
 */

(function () {
  'use strict';

  /* ─── Utility ─────────────────────────────────────── */
  const $ = (sel, ctx = document) => ctx.querySelector(sel);
  const $$ = (sel, ctx = document) => [...ctx.querySelectorAll(sel)];
  const on = (el, ev, fn, opts) => el && el.addEventListener(ev, fn, opts);

  /* ─── Mobile Navigation ────────────────────────────── */
  function initMobileNav() {
    const toggle = $('.mobile-nav-toggle');
    const nav    = $('#MobileNav');
    const close  = $('.mobile-nav__close');

    if (!toggle || !nav) return;

    function openNav() {
      nav.classList.add('open');
      nav.style.display = 'block';
      nav.setAttribute('aria-hidden', 'false');
      toggle.setAttribute('aria-expanded', 'true');
      document.body.style.overflow = 'hidden';
      // Focus the close button for accessibility
      setTimeout(() => close.focus(), 100);
    }
    function closeNav() {
      nav.classList.remove('open');
      nav.style.display = 'none';
      nav.setAttribute('aria-hidden', 'true');
      toggle.setAttribute('aria-expanded', 'false');
      document.body.style.overflow = '';
      // Return focus to the toggle button
      toggle.focus();
    }

    on(toggle, 'click', openNav);
    on(close,  'click', closeNav);
    on(nav, 'click', (e) => { if (e.target === nav) closeNav(); });
    on(document, 'keydown', (e) => { if (e.key === 'Escape') closeNav(); });
  }

  /* ─── Variant Selection (PDP) ──────────────────────── */
  function initVariantSelection() {
    const variantGroups = $$('.variant-group[data-option-position]');
    if (!variantGroups.length) return;

    const form        = $('#product-form');
    const variantInput = form ? form.querySelector('input[name="id"]') : null;

    // Get all variants from the page (injected via Liquid)
    const variantsEl = $('#product-variants-json');
    let variants = [];
    if (variantsEl) {
      try { variants = JSON.parse(variantsEl.textContent); } catch(e) {}
    }

    variantGroups.forEach(group => {
      const position = parseInt(group.dataset.optionPosition, 10);
      const pills    = $$('.variant-pill', group);
      const label    = $(`#label-${position}`);

      pills.forEach(pill => {
        on(pill, 'click', () => {
          pills.forEach(p => p.classList.remove('selected'));
          pill.classList.add('selected');
          if (label) label.textContent = pill.dataset.value;
          pill.scrollIntoView({ behavior: 'smooth', inline: 'nearest', block: 'nearest' });

          // Try to update the hidden variant ID
          updateVariantId();
        });
      });
    });

    function updateVariantId() {
      if (!variants.length || !variantInput) return;
      const selectedOptions = variantGroups.map(group => {
        const selected = $('.variant-pill.selected', group);
        return selected ? selected.dataset.value : null;
      });

      const match = variants.find(v =>
        v.options.every((opt, i) => opt === selectedOptions[i])
      );
      if (match) {
        variantInput.value = match.id;
        // Update price display if elements exist
        const priceEl  = $('.pdp__price');
        const wasEl    = $('.pdp__was');
        const saveEl   = $('.pdp__save');
        const skuEl    = $('.pdp__sku');
        const addBtn   = form.querySelector('[name="add"]');

        if (priceEl)  priceEl.textContent  = formatMoney(match.price);
        if (wasEl)    wasEl.textContent    = match.compare_at_price ? formatMoney(match.compare_at_price) : '';
        if (saveEl)   saveEl.textContent   = match.compare_at_price > match.price
          ? `Save ${formatMoney(match.compare_at_price - match.price)}` : '';
        if (skuEl && match.sku) skuEl.textContent = `SKU: ${match.sku}`;
        if (addBtn) {
          addBtn.disabled       = !match.available;
          addBtn.textContent    = match.available ? 'Add to Cart' : 'Sold Out';
        }

        // Update main image if variant has one
        if (match.featured_image) {
          const mainImg = $('#pdp-main-img');
          if (mainImg) {
            mainImg.srcset = '';
            mainImg.sizes  = '';
            mainImg.src    = match.featured_image.src;
          }
        }

        // ═══════════════════════════════════════════════
        // CONVERSION FIX: Update stock scarcity alert
        // ═══════════════════════════════════════════════
        const stockAlert = $('#stock-alert');
        if (match.inventory_management === 'shopify') {
          const qty = match.inventory_quantity;
          if (qty > 0 && qty <= 10) {
            if (!stockAlert) {
              // Create stock alert if it doesn't exist
              const alert = document.createElement('div');
              alert.className = qty <= 5 ? 'pdp__stock-alert pdp__stock-alert--critical' : 'pdp__stock-alert';
              alert.id = 'stock-alert';
              alert.innerHTML = `⚠️ Only ${qty} left in stock!`;
              const priceBlock = $('#pdp-price-block');
              if (priceBlock) priceBlock.after(alert);
            } else {
              // Update existing stock alert
              stockAlert.className = qty <= 5 ? 'pdp__stock-alert pdp__stock-alert--critical' : 'pdp__stock-alert';
              stockAlert.innerHTML = `⚠️ Only ${qty} left in stock!`;
            }
          } else if (stockAlert) {
            stockAlert.remove();
          }
        } else if (stockAlert) {
          stockAlert.remove();
        }
      }
    }
  }

  function formatMoney(cents) {
    return '$' + (cents / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  /* ─── Variant Slider (arrow-controlled scroll) ─────── */
  function initVariantSliders() {
    const sliders = $$('.variant-slider');
    if (!sliders.length) return;

    sliders.forEach(slider => {
      const track = $('.variant-group', slider);
      const prev  = $('.variant-slider__arrow--prev', slider);
      const next  = $('.variant-slider__arrow--next', slider);
      if (!track || !prev || !next) return;

      const scrollAmount = () => Math.max(track.clientWidth * 0.7, 80);

      function updateArrows() {
        const overflowing = track.scrollWidth > track.clientWidth + 1;
        slider.classList.toggle('has-overflow', overflowing);
        if (!overflowing) return;

        prev.disabled = track.scrollLeft <= 1;
        next.disabled = track.scrollLeft + track.clientWidth >= track.scrollWidth - 1;
      }

      on(prev, 'click', () => {
        track.scrollBy({ left: -scrollAmount(), behavior: 'smooth' });
      });
      on(next, 'click', () => {
        track.scrollBy({ left: scrollAmount(), behavior: 'smooth' });
      });
      on(track, 'scroll', updateArrows, { passive: true });
      on(window, 'resize', updateArrows);

      // Re-check after fonts/images settle and on initial paint
      updateArrows();
      setTimeout(updateArrows, 150);
      if (window.ResizeObserver) {
        new ResizeObserver(updateArrows).observe(track);
      }
    });
  }

  /* ─── PDP Image Gallery ────────────────────────────── */
  function initGallery() {
    const thumbs  = $$('.pdp__thumb');
    const mainImg = $('#pdp-main-img');
    if (!thumbs.length || !mainImg) return;

    thumbs.forEach(thumb => {
      on(thumb, 'click', () => {
        thumbs.forEach(t => t.classList.remove('active'));
        thumb.classList.add('active');
        mainImg.srcset = '';
        mainImg.sizes  = '';
        mainImg.src    = thumb.dataset.src;
      });
    });
  }

  /* ─── Quantity Selectors ───────────────────────────── */
  function initQuantitySelectors() {
    on(document, 'click', (e) => {
      const btn = e.target.closest('.qty-btn');
      if (!btn) return;

      const input  = btn.closest('.quantity-selector')?.querySelector('.qty-input');
      if (!input) return;

      const action = btn.dataset.action;
      const val    = parseInt(input.value, 10) || 1;
      const min    = parseInt(input.min, 10)   || 0;
      const max    = parseInt(input.max, 10)   || 999;

      if (action === 'plus')  input.value = Math.min(val + 1, max);
      if (action === 'minus') input.value = Math.max(val - 1, min);

      input.dispatchEvent(new Event('change', { bubbles: true }));
    });

    // Sync PDP quantity input with hidden field for Buy Now button
    const pdpQtyInput = $('#pdp-quantity');
    const hiddenQtyInput = $('#pdp-qty-hidden');
    
    if (pdpQtyInput && hiddenQtyInput) {
      on(pdpQtyInput, 'change', () => {
        hiddenQtyInput.value = pdpQtyInput.value;
      });
    }
  }

  /* ─── Add to Cart (AJAX) ───────────────────────────── */
  function initCartForms() {
    on(document, 'submit', async (e) => {
      const form = e.target.closest('form[action="/cart/add"]');
      if (!form) return;

      // Only AJAX on product cards; full submit on PDP
      if (form.id === 'product-form') return;

      e.preventDefault();

      const btn  = form.querySelector('[type="submit"]');
      const id   = form.querySelector('[name="id"]')?.value;
      const qty  = form.querySelector('[name="quantity"]')?.value || 1;

      if (!id) return;

      const originalText = btn?.textContent || 'Add to Cart';
      if (btn) { btn.textContent = 'Adding…'; btn.disabled = true; }

      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id, quantity: parseInt(qty, 10) }),
        });

        if (!res.ok) throw new Error('Cart add failed');

        if (btn) { btn.textContent = '✓ Added'; btn.style.background = '#30D158'; }
        updateCartCount();

        setTimeout(() => {
          if (btn) {
            btn.textContent = originalText;
            btn.style.background = '';
            btn.disabled = false;
          }
        }, 2000);

      } catch (err) {
        console.error(err);
        if (btn) { btn.textContent = originalText; btn.disabled = false; }
      }
    });
  }

  async function updateCartCount() {
    try {
      const res  = await fetch('/cart.js');
      const cart = await res.json();
      let badge = $('.cart-count');

      // If badge doesn't exist but we have items, create it
      if (!badge && cart.item_count > 0) {
        const cartLink = $('.cart-badge');
        if (cartLink) {
          badge = document.createElement('span');
          badge.className = 'cart-count';
          cartLink.appendChild(badge);
        }
      }

      if (badge) {
        badge.textContent = cart.item_count;
        badge.style.display = cart.item_count > 0 ? 'flex' : 'none';
      }
    } catch(e) {}
  }

  /* ─── Scroll-Triggered Animations ─────────────────── */
  function initScrollAnimations() {
    if (!('IntersectionObserver' in window)) return;

    const targets = $$('.product-card, .cat-card, .promo-banner, .trust-item, .article-card, .hero-product-card');

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry, i) => {
        if (entry.isIntersecting) {
          entry.target.style.animationDelay = `${(i % 4) * 60}ms`;
          entry.target.classList.add('animate-fade-up');
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.05, rootMargin: '0px 0px -20px 0px' });

    targets.forEach(el => {
      el.style.opacity = '0';
      observer.observe(el);
    });
  }

  /* ─── Sticky Header Shadow (Disabled for static header) ─── */
  function initStickyHeader() {
    // Header is now static, so we don't need to toggle shadows on scroll.
    return;
  }

  /* ─── Search Form ──────────────────────────────────── */
  function initSearch() {
    const form      = $('.nav-search form');
    const input     = $('.nav-search input[type="search"]');
    const selectEl  = $('.nav-search select');

    if (!form || !input) return;

    on(form, 'submit', (e) => {
      const q = input.value.trim();
      if (!q) { e.preventDefault(); input.focus(); return; }

      // Append category filter if selected
      if (selectEl && selectEl.value) {
        const hidden = document.createElement('input');
        hidden.type  = 'hidden';
        hidden.name  = 'type';
        hidden.value = 'product';
        form.appendChild(hidden);
      }
    });
  }

  /* ─── Cart: Remove Item ────────────────────────────── */
  function initCartPage() {
    if (!document.querySelector('.main-cart')) return;

    on(document, 'change', async (e) => {
      const input = e.target.closest('.cart-item .qty-input');
      if (!input) return;

      const cartItem = input.closest('.cart-item');
      const variantId = cartItem?.dataset.variantId;
      if (!variantId) return;

      const qty = parseInt(input.value, 10);
      if (isNaN(qty) || qty < 0) return;

      try {
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: variantId, quantity: qty }),
        });
        if (qty === 0) cartItem.remove();
        updateCartCount();
        // Reload page to recalculate totals
        if (qty !== 0) window.location.reload();
      } catch(err) { console.error(err); }
    });
  }

  /* ─── Announcement Bar Dismiss ─────────────────────── */
  function initAnnouncement() {
    const bar = $('.announcement-bar');
    if (!bar) return;
    if (sessionStorage.getItem('announcement-dismissed')) {
      bar.style.display = 'none';
    }
  }

  /* ─── Collapsible Description & Accordion ─────────── */
  function initAccordions() {
    $$('.pdp__desc-toggle').forEach(toggle => {
      const panelId = toggle.getAttribute('aria-controls');
      if (!panelId) return;
      const panel = document.getElementById(panelId);
      if (!panel) return;

      on(toggle, 'click', () => {
        const isOpen = toggle.getAttribute('aria-expanded') === 'true';
        toggle.setAttribute('aria-expanded', String(!isOpen));
        panel.classList.toggle('open', !isOpen);
      });
    });
  }

  /* ─── Buy Now Button (Direct Checkout) ────────────── */
  function initBuyNow() {
    const buyNowBtn = $('#pdp-buy-now');
    if (!buyNowBtn) return;

    on(buyNowBtn, 'click', async (e) => {
      e.preventDefault();
      
      const variantId = $('#pdp-variant-id')?.value;
      const quantity = parseInt($('#pdp-quantity')?.value || 1, 10);
      
      if (!variantId) return;
      
      // Disable button and show loading state
      const originalText = buyNowBtn.textContent;
      buyNowBtn.disabled = true;
      buyNowBtn.textContent = 'Processing...';
      
      try {
        // Add to cart first
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ 
            id: variantId, 
            quantity: quantity 
          })
        });
        
        if (!response.ok) throw new Error('Failed to add to cart');
        
        // Redirect to checkout
        window.location.href = '/checkout';
        
      } catch (error) {
        console.error('Buy Now error:', error);
        alert('Sorry, there was an error. Please try again.');
        buyNowBtn.disabled = false;
        buyNowBtn.textContent = originalText;
      }
    });
  }

  /* ─── Init ─────────────────────────────────────────── */
  function init() {
    initMobileNav();
    initVariantSelection();
    initVariantSliders();
    initGallery();
    initQuantitySelectors();
    initCartForms();
    initBuyNow();
    initScrollAnimations();
    initStickyHeader();
    initSearch();
    initCartPage();
    initAnnouncement();
    initAccordions();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
